import { type FilterControl, type FilterOption, filterUrlState, type UrlValues } from 'noddi-ui-web';
import type { QueryParamConfig } from 'use-query-params';

// Controls the builders cannot express for the inventory lists, for `unit.compose`: each declares its URL
// params and resolves to one control plus the values its params take.

type ControlText = { title: string; description?: string };

type SetUrlValues = (changes: (latest: UrlValues) => UrlValues) => void;

type Composite<TValues> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each composite owns its value types, as `compose` takes them
  urlParams: Record<string, QueryParamConfig<any, any>>;
  resolve: (values: UrlValues, setValues: SetUrlValues) => { control: FilterControl } & TValues;
};

/**
 * One value into a scalar param, its exclusions into a list param of another name (`loadIndexGte` +
 * `excludedLoadIndex`: a minimum, but exact indexes excluded), which `singleSelect` does not pair.
 */
export const singleWithExclusions = <T extends string | number>({
  key,
  excludedKey,
  options,
  text
}: {
  key: string;
  excludedKey: string;
  options: FilterOption<T>[];
  text: ControlText;
}): Composite<{ include: T | undefined; exclude: T[] }> => {
  const urlKeys = { include: filterUrlState.urlKey(key), exclude: filterUrlState.urlKey(excludedKey) };
  const byText = new Map(options.map((option) => [String(option.value), option.value]));
  const parse = (value: string) => byText.get(value);
  const parseAll = (values: (string | number)[]) =>
    values.flatMap((value) => {
      const parsed = parse(String(value));
      return parsed === undefined ? [] : [parsed];
    });
  return {
    urlParams: {
      [urlKeys.include]: filterUrlState.stored<T>(String, parse),
      [urlKeys.exclude]: filterUrlState.excluded(parse)
    },
    resolve: (values, setValues) => {
      const excluded = (values[urlKeys.exclude] as T[] | undefined) ?? [];
      const stored = (values[urlKeys.include] as T | null | undefined) ?? undefined;
      // An excluded value is never the selection.
      const value = stored !== undefined && !excluded.includes(stored) ? stored : undefined;
      const write = (include: T | undefined, exclude: T[]) =>
        setValues(() => ({ [urlKeys.include]: include, [urlKeys.exclude]: exclude }));
      return {
        control: {
          ...text,
          id: key,
          type: 'single-select',
          options,
          canExclude: true,
          value: { include: value ?? null, exclude: excluded },
          onChange: (next) => {
            const exclude = parseAll(next.exclude);
            const [include] = next.include == null ? [] : parseAll([next.include]);
            write(include !== undefined && !exclude.includes(include) ? include : undefined, exclude);
          },
          reset: () => write(undefined, []),
          isInitial: values[urlKeys.include] === undefined && excluded.length === 0
        },
        include: value,
        exclude: excluded
      };
    }
  };
};
